import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { ArticleService } from 'app/shared/article.service';
import { UtilisateurService } from 'app/shared/utilisateur.service';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { ConvertToSlugPipe } from './convert-to-slug.pipe';
import { Article } from 'app/models/Article';
import { HttpClient } from '@angular/common/http';

export const ArticleResolver = (route: ActivatedRouteSnapshot) => {
  const articleService = inject(ArticleService);
  const utilisateurService = inject(UtilisateurService);

  const id = route.paramMap.get('id') || route.params['id'];
  const nom = route.paramMap.get('nom') || route.params['malformedSlug'];

  if (!id) return of([null, null, false]);

  return articleService.getArticle(id).pipe(
    switchMap((article) => {
      if (!article) return of([null, null, false]);
      
      // Additional check that this is actually an article
      if (article.type !== 'article') {
        return of([null, null, false]);
      }
      return utilisateurService.getUtilisateur(article.auteur).pipe(
        map((auteur) => [article, auteur]),
        catchError(() => of([article, null])),
      );
    }),
    catchError(() => of([null, null, false]))
  );
};

export const NewsResolver = (route: ActivatedRouteSnapshot) => {
  const articleService = inject(ArticleService);
  const utilisateurService = inject(UtilisateurService);
  const router = inject(Router);
  const convertToSlugPipe = new ConvertToSlugPipe();

  // Verify we're in the news context
  if (!route.url.some(segment => segment.path === 'news')) {
    return of([null, null, false]);
  }

  const isMalformed = !!route.params['malformedSlug'];
  const id = route.paramMap.get('id') || route.params['id'];
  const nom = route.paramMap.get('nom') || route.params['malformedSlug'];

  if (!id) return of([null, null, false]);

  return articleService.getArticle(id).pipe(
    switchMap((article) => {
      if (!article) return of([null, null, false]);
      
      // Additional check that this is actually news
      if (article.type !== 'news') {
        return of([null, null, false]);
      }
      
      const expectedSlug = convertToSlugPipe.transform(article.nom);
      const actualSlug = convertToSlugPipe.transform(nom);
      const isSlugValid = expectedSlug === actualSlug;
      
      const isValid = isSlugValid && !isMalformed;

      return utilisateurService.getUtilisateur(article.auteur).pipe(
        map((auteur) => [article, auteur, isValid]),
        catchError(() => of([article, null, isValid])),
      );
    }),
    catchError(() => of([null, null, false]))
  );
};

export const ArticlePublicResolver = (route: ActivatedRouteSnapshot): Observable<[any, any, boolean]> => {
  const http = inject(HttpClient);
  const convertToSlugPipe = new ConvertToSlugPipe();

  const id = route.paramMap.get('id') || route.params['id'];
  const nom = route.paramMap.get('nom') || route.params['malformedSlug'];

  if (!id) return of([null, null, false] as [any, any, boolean]);

  return http.get<any>('assets/articles.json').pipe(
    map(data => {
      console.log('Articles disponibles:', data.articles.map(a => ({ id: a.id, nom: a.nom })));
      return data.articles.find(a => a.id === id);
    }),
    switchMap((article) => {
      if (!article) {
        console.warn('Article non trouvé avec ID:', id);
        return of([null, null, false] as [any, any, boolean]);
      }
      
      console.log('Article trouvé:', article.nom);
      
      // Additional check that this is actually an article
      if (article.type !== 'article') {
        console.warn('Type incorrect pour l\'article:', article.type);
        return of([null, null, false] as [any, any, boolean]);
      }

      // Validation du slug si fourni
      let isSlugValid = true;
      if (nom) {
        const expectedSlug = convertToSlugPipe.transform(article.nom);
        const actualSlug = convertToSlugPipe.transform(nom);
        isSlugValid = expectedSlug === actualSlug;
        
        if (!isSlugValid) {
          console.warn('Slug invalide:', { expected: expectedSlug, actual: actualSlug });
        }
      }
      
    
      
      // Pas d'auteur, retourner l'article sans auteur
      return of([article, null, isSlugValid] as [any, any, boolean]);
    }),
    catchError((error) => {
      console.error('Erreur lors de la récupération de l\'article public:', error);
      return of([null, null, false] as [any, any, boolean]);
    })
  );
};